/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Gets an environment variable value
 * @param key - The environment variable key
 * @returns The environment variable value or undefined
 */
export function getEnvVar(key: string): string | undefined {
	return process.env[key];
}

/**
 * Gets an environment variable value, throwing if not set
 * @param key - The environment variable key
 * @returns The environment variable value
 * @throws Error if the environment variable is not set
 */
export function getEnvVarRequired(key: string): string {
	const value = process.env[key];
	if (value === undefined || value === "") {
		throw new Error(`Environment variable ${key} is required but not set`);
	}
	return value;
}

/**
 * Gets an environment variable value with optional default
 * @param key - The environment variable key
 * @param defaultValue - Optional default value if not set
 * @returns The environment variable value or default
 */
export function getEnvVarOptional(key: string, defaultValue?: string): string | undefined {
	const value = process.env[key];
	if (value === undefined || value === "") {
		return defaultValue;
	}
	return value;
}

/**
 * Gets EPIC_BEARER_TOKEN_COOKIE_NAME environment variable
 * @returns The cookie name for bearer token
 * @throws Error if not set
 */
export function EPIC_BEARER_TOKEN_COOKIE_NAME(): string {
	return getEnvVarRequired("EPIC_BEARER_TOKEN_COOKIE_NAME");
}

/**
 * Gets EPIC_BEARER_TOKEN_COOKIE_DOMAIN environment variable
 * @returns The cookie domain for bearer token
 * @throws Error if not set
 */
export function EPIC_BEARER_TOKEN_COOKIE_DOMAIN(): string {
	return getEnvVarRequired("EPIC_BEARER_TOKEN_COOKIE_DOMAIN");
}

/**
 * Gets EPIC_BEARER_TOKEN_COOKIE_PATH environment variable
 * @returns The cookie path for bearer token (defaults to "/")
 */
export function EPIC_BEARER_TOKEN_COOKIE_PATH(): string {
	return getEnvVarOptional("EPIC_BEARER_TOKEN_COOKIE_PATH", "/") ?? "/";
}

/**
 * Gets EPIC_ACCOUNT_PORTAL_BASE_URL environment variable
 * @returns The account portal base URL
 * @throws Error if not set
 */
export function EPIC_ACCOUNT_PORTAL_BASE_URL(): string {
	return getEnvVarRequired("EPIC_ACCOUNT_PORTAL_BASE_URL");
}

/**
 * Gets EPIC_ACCOUNT_PORTAL_BASE_PATH environment variable
 * @returns The account portal base path
 * @throws Error if not set
 */
export function EPIC_ACCOUNT_PORTAL_BASE_PATH(): string {
	return getEnvVarRequired("EPIC_ACCOUNT_PORTAL_BASE_PATH");
}

/**
 * Gets EPIC_SERVER_URL environment variable
 * @returns The Epic server URL
 * @throws Error if not set
 */
export function EPIC_SERVER_URL(): string {
	return getEnvVarRequired("EPIC_SERVER_URL");
}

/**
 * Gets EPIC_SERVICE_CLIENT_ID environment variable
 * @returns The Epic service client ID
 * @throws Error if not set
 */
export function EPIC_SERVICE_CLIENT_ID(): string {
	return getEnvVarRequired("EPIC_SERVICE_CLIENT_ID");
}

/**
 * Gets EPIC_SERVICE_CLIENT_SECRET environment variable
 * @returns The Epic service client secret
 * @throws Error if not set
 */
export function EPIC_SERVICE_CLIENT_SECRET(): string {
	return getEnvVarRequired("EPIC_SERVICE_CLIENT_SECRET");
}

/**
 * Gets EPIC_SERVICE_ACCOUNT_URL environment variable
 * @returns The Epic service account URL
 * @throws Error if not set
 */
export function EPIC_SERVICE_ACCOUNT_URL(): string {
	return getEnvVarRequired("EPIC_SERVICE_ACCOUNT_URL");
}

/**
 * Gets EPIC_SERVICE_CLIENT_TOKEN_TIMEDRIFT environment variable
 * @returns The Epic service client token time drift in milliseconds (defaults to 10000)
 */
export function EPIC_SERVICE_CLIENT_TOKEN_TIMEDRIFT(): number {
	const value = getEnvVarOptional("EPIC_SERVICE_CLIENT_TOKEN_TIMEDRIFT", "10000");
	return Number.parseInt(value ?? "10000", 10);
}
